import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import MenuList from '@components/MenuList';
import Tooltip from '@components/Tooltip';
import { ModalConfirm } from '@components/Modal';
import { useTranslation, usePlaceholderResolver } from '@hooks';
import { serializePlaceholdersToHtml } from '@helpers/text-editor';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { inboxTemplatesService } from '@services';
import { alertActions, inboxTemplatesActions } from '@actions';
import usePermissions from '@hooks/usePermissions';
import ManageTemplateModal, { TEMPLATE_MODAL_MODE } from './ManageTemplateModal';
import { StyledSaveIcon, StyledDeleteIcon } from './styles';

const buildPreviewSubstitutions = (tags, company) => ({
    name: tags?.firstName,
    last_name: tags?.lastName,
    ...(company && {
        location: company.locationName,
        brand: company.brand?.title,
        address_line_1: company.addressLine1,
        city: company.city,
        country: company.country,
    }),
});

const applyPreviewSubstitutions = (message, substitutions) => {
    const tokens = Object.keys(substitutions);
    if (tokens.length === 0) return message ?? '';
    const regex = new RegExp(`\\[(${tokens.join('|')})\\]`, 'g');
    return (message ?? '').replace(regex, (_, token) => substitutions[token] ?? '');
};

const InboxTemplateMenu = ({ disabled, content, setContent, tags }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const placeholderResolver = usePlaceholderResolver(PLACEHOLDER_CONTEXT.INBOX);
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [anchorElDelete, setAnchorElDelete] = useState(null);
    const [anchorElEdit, setAnchorElEdit] = useState(null);

    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isEditFormOpen, setEditFormOpen] = useState(false);
    const [isEditConfirmOpen, setEditConfirmOpen] = useState(false);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editFormValues, setEditFormValues] = useState(null);

    const { data } = useSelector(state => state.inboxTemplates);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const user = useSelector(state => state.auth?.user);
    const { hasFullAccess } = usePermissions();
    const canManageTemplates = hasFullAccess('messaging');

    useEffect(() => {
        dispatch(inboxTemplatesActions.getAll());
    }, []);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
            setAnchorElDelete(null);
            setAnchorElEdit(null);
        }
    };
    const handleOpenDelete = event => {
        setAnchorElDelete(event.currentTarget);
        setAnchorElEdit(null);
    };
    const handleOpenEdit = event => {
        setAnchorElEdit(event.currentTarget);
        setAnchorElDelete(null);
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const onClickTemplate = template => {
        const company = tags?.locations?.length === 1 ? companies.find(c => c.companyId === tags.locations[0]) : null;
        const newMessage = applyPreviewSubstitutions(template.message, buildPreviewSubstitutions(tags, company));
        editor?.commands?.setContent(serializePlaceholdersToHtml(newMessage, placeholderResolver), {
            emitUpdate: false,
        });
        setContent(JSON.stringify(editor?.getJSON() || {}));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const onSubmitEditForm = values => {
        setEditFormValues(values);
        setEditFormOpen(false);
        setEditConfirmOpen(true);
    };

    const onEditTemplate = async () => {
        try {
            await inboxTemplatesService.addOrUpdateInboxTemplate({
                ...selectedTemplate,
                name: editFormValues.name,
                shared: editFormValues.shared,
                message: editFormValues.message,
            });
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            await dispatch(inboxTemplatesActions.getAll());
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.editFail')));
        }
        setEditConfirmOpen(false);
        setAnchorElEdit(null);
        setAnchorElMain(null);
        setSelectedTemplate(null);
        setEditFormValues(null);
    };

    const onDeleteTemplate = async () => {
        try {
            await inboxTemplatesService.deleteInboxTemplate(selectedTemplate.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            await dispatch(inboxTemplatesActions.getAll());
            setDeleteModalOpen(false);
            setAnchorElDelete(null);
            setAnchorElMain(null);
            setSelectedTemplate(null);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    const ACTIONS = {
        INSERT: template => () => onClickTemplate(template),
        EDIT: template => () => {
            setSelectedTemplate(template);
            setEditFormOpen(true);
        },
        DELETE: template => () => {
            setSelectedTemplate(template);
            setDeleteModalOpen(true);
        },
    };

    function mapTemplates(data, action) {
        return data
            .filter(t => {
                if (action === 'INSERT') return true;
                return (!canManageTemplates && !t.shared) || canManageTemplates;
            })
            .map(template => ({
                icon: null,
                text: template.name,
                sub: {},
                onClick: ACTIONS[action](template),
            }));
    }

    const MAIN_MENU = [
        ...mapTemplates(data, 'INSERT'),
        ...[
            {
                divider: true,
                icon: <StyledSaveIcon />,
                text: t('RespondTemplate.saveReplyAsTemplate'),
                sub: {
                    header: t('RespondTemplate.overwriteTemplate'),
                    list: [
                        ...mapTemplates(data, 'EDIT'),
                        {
                            divider: true,
                            icon: <StyledSaveIcon />,
                            text: t('RespondTemplate.saveAsNewTemplate'),
                            sub: {},
                            onClick: () => setCreateModalOpen(true),
                        },
                    ],
                },
                anchorEl: anchorElEdit,
                onClick: handleOpenEdit,
            },
            {
                icon: <StyledDeleteIcon />,
                text: t('RespondTemplate.deleteTemplate'),
                sub: {
                    header: t('RespondTemplate.deleteTemplate'),
                    list: mapTemplates(data, 'DELETE'),
                },
                anchorEl: anchorElDelete,
                onClick: handleOpenDelete,
            },
        ],
    ];

    return (
        <>
            <MenuList
                options={MAIN_MENU}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('RespondTemplate.insertTemplate')}
                menuButton={
                    <Tooltip title={t('RespondTemplate.insertTemplate')} placement="top">
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={handleOpenMain}
                            disabled={disabled}
                            aria-label={t('RespondTemplate.insertTemplate')}
                            sx={{
                                height: 18,
                                width: 18,
                                padding: '2px !important',
                                '& svg': { color: 'var(--text-secondary)', fontSize: 16 },
                            }}
                        >
                            <ChatBubbleRoundedIcon />
                        </IconButton>
                    </Tooltip>
                }
            />
            {isDeleteModalOpen ? (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteTemplate}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            ) : null}
            {isEditFormOpen ? (
                <ManageTemplateModal
                    user={user}
                    content={content}
                    mode={TEMPLATE_MODAL_MODE.EDIT}
                    selectedTemplate={selectedTemplate}
                    isOpen={isEditFormOpen}
                    setOpen={setEditFormOpen}
                    setAnchorElEdit={setAnchorElEdit}
                    onConfirmEdit={onSubmitEditForm}
                />
            ) : null}
            {isEditConfirmOpen ? (
                <ModalConfirm
                    isOpen={isEditConfirmOpen}
                    handleClose={() => {
                        setEditConfirmOpen(false);
                        setEditFormValues(null);
                    }}
                    title={t('RespondTemplate.editTemplate')}
                    description={t('RespondTemplate.editTemplateDescription')}
                    onPrimaryAction={onEditTemplate}
                    primaryActionText={t('buttons.save')}
                />
            ) : null}
            {isCreateModalOpen ? (
                <ManageTemplateModal
                    user={user}
                    content={content}
                    isOpen={isCreateModalOpen}
                    setOpen={setCreateModalOpen}
                    setAnchorElEdit={setAnchorElEdit}
                />
            ) : null}
        </>
    );
};

export default InboxTemplateMenu;
